import $ from 'jquery';
import wurl from 'wurl';

const SELECTORS = {
    SELECT: '.js-selectView-select',
};

/**
 * @fileOverview View handles updating the visual elements on the page
 * Controls the display of content depending on which tab is selected
 */
export default class SelectView {
    /**
     * Construct the view and run initialization
     *
     * @param element
     */
    constructor(element) {
        /**
         * Check to see if elements are passed in as jQuery objects or strings
         */
        this.$element = (element instanceof $) ? element : $(element);

        /**
         * If element doesn't exist, exit early
         */
        if (!this.$element.length) {
            return;
        }

        this.init();
    }

    /**
     * Initialize the view
     *
     * @method init
     */
    init() {
        return this.setupHandlers()
                   .createChildren()
                   .layout()
                   .enable();
    }

    /**
     * Binds the scope of handler functions
     * Should be run once on view initialization
     *
     * @method setupHandlers
     * @returns {SelectView}
     */
    setupHandlers() {
        this.handleChange = this.onChange.bind(this);

        return this;
    }

    /**
     * Create any child objects and references to DOM elements
     * Should only be run once on init of the view
     *
     * @method createChildren
     * @returns {SelectView}
     */
    createChildren() {
        this.$select = this.$element.find(SELECTORS.SELECT);
        this.params = wurl('?') || {};

        return this;
    }

    /**
     * Update the DOM to reflect the current state of the model
     *
     * @method layout
     * @returns {SelectView}
     */
    layout() {
        this.updateSelected();
        return this;
    }

    /**
     * Disable, update the DOM, and then re-enable to create handlers for new DOM
     *
     * @method render
     */
    render() {
        this.disable();
        this.layout();
        this.enable();
    }

    /**
     * Enables the view
     * Performs binding to handlers
     * Exits early if already enabled
     *
     * @method enable
     * @returns {SelectView}
     */
    enable() {
        if (this.isEnabled) {
            return this;
        }

        this.isEnabled = true;

        for (let i = 0; i < this.$select.length; i++) {
            this.$select[i].addEventListener('change', this.handleChange);
        }

        return this;
    }

    /**
     * Disables the view
     * Tears down any event binding to handlers
     * Exits early if it is already disabled
     *
     * @method disable
     * @returns {SelectView}
     */
    disable() {
        if (!this.isEnabled) {
            return this;
        }

        for (let i = 0; i < this.$select.length; i++) {
            this.$select[i].removeEventListener('change', this.handleChange);
        }

        this.isEnabled = false;

        return this;
    }

    /**
     * Destroys the view
     * Tears down any events, handlers, elements
     * Should be called when the object should be left unused
     *
     * @method destroy
     * @returns {SelectView}
     */
    destroy() {
        this.disable();

        return this;
    }

    /**
     * Handles select change event
     * Creates a new query string based upon the item selected
     * and refreshes the page with the new url
     *
     * @param event
     * @returns {SelectView}
     */
    onChange(event) {
        event.preventDefault();
        const $target = $(event.currentTarget);
        const name = $target.data('selectQueryName');

        this.params[name] = $target.find(':selected').val();
        window.location.href = `?${$.param(this.params)}`;

        return this;
    }

    /**
     * Update the selected item with the option from query string if applicable
     *
     * @returns {SelectView}
     */
    updateSelected() {
        this.$select.each((key) => {
            const $select = $(this.$select[key]);
            const name = $select.data('selectQueryName');
            if (name && this.params[name]) {
                $select.find(`[value="${this.params[name]}"]`).prop('selected', true);
            } else {
                $select.first().prop('selected', true);
            }
        });

        return this;
    }
}
